import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { applyTransaction } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConfirmDialogComponent } from '@y42/core/dialog/confirm-dialog';
import { ExportQuery } from '@y42/data-export';
import { DataLineageDiagramDataService } from '@y42/data-lineage/diagram';
import { AllDatasourcesQuery, AllDatasourceTablesQuery, DatasourceTableUIModel } from '@y42/datasource';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitChangesConfig, GitQuery } from '@y42/git';
import { Y42FeatureType } from '@y42/json-schema';
import { isNullish } from '@y42/util/nullish';
import uniq from 'lodash/uniq';
import { combineLatest, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import {
  OrchestrationQuery,
  OrchestrationRunQuery,
  OrchestrationRunStore,
  OrchestrationRunStoreModel,
  OrchestrationRunUIModel,
  OrchestrationStore,
  OrchestrationStoreModel,
} from '../../state';
import {
  isDynamicPayload,
  isLocationPayload,
  isStaticPayload,
  OrchestrationRunHttpModel,
  OrchestrationRunHttpService,
  OrchestrationRunsPayload,
  TriggerOrchestrationLocationPayload,
  TriggerOrchestrationRunDynamicPayload,
  TriggerOrchestrationRunPayload,
  TriggerOrchestrationRunStaticPayload,
} from './orchestration-run-http.service';
import { OrchestrationRunRealtimeService } from './orchestration-run-realtime.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class OrchestrationRunService {
  constructor(
    private readonly store: OrchestrationRunStore,

    private readonly orchestrationQuery: OrchestrationQuery,
    private readonly orchestrationStore: OrchestrationStore,

    private readonly orchestrationRunQuery: OrchestrationRunQuery,

    private readonly orchestrationRunHttpService: OrchestrationRunHttpService,
    private readonly orchestrationRunRealtimeService: OrchestrationRunRealtimeService,
    private readonly dataLineageDiagramDataService: DataLineageDiagramDataService,

    private readonly allDatasourcesQuery: AllDatasourcesQuery,
    private readonly allDatasourceTablesQuery: AllDatasourceTablesQuery,
    private readonly exportQuery: ExportQuery,

    private readonly gitQuery: GitQuery,

    private readonly dialog: MatDialog,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    this.maintainFreshestOrchestrationRuns();
  }

  setActiveRun(run: OrchestrationRunUIModel | null) {
    this.store.setActive(run?.run_id || null);
  }

  triggerOrchestrationRun(config: TriggerOrchestrationRunPayload) {
    let runConfig: TriggerOrchestrationRunDynamicPayload | TriggerOrchestrationRunStaticPayload | undefined = undefined;
    if (isLocationPayload(config)) {
      const orchestration = this.orchestrationQuery.getEntity(config.location);
      if (!orchestration) {
        this.errorHandlerService.error(
          '[OrchestrationRunService.triggerOrchestrationRun]: could not trigger orchestration run - location not found',
          { toast: 'Could not find orchestration to trigger run' },
        );
        return of(null);
      }
      runConfig = orchestration.config;
    } else {
      runConfig = config;
    }

    if (isNullish(runConfig)) {
      this.errorHandlerService.error(
        '[OrchestrationRunService.triggerOrchestrationRun]: could not trigger orchestration run - no config',
        { toast: 'No config to trigger run' },
      );
      return of(null);
    }

    const staticOrDynamicConfig = runConfig;
    return this.calculateAssetsPathsToCheck(staticOrDynamicConfig).pipe(
      map((assetsPathsToCheck) => {
        const pathsToCheck: GitChangesConfig[] = assetsPathsToCheck.flatMap((path) => {
          const entity = this.getEntity(path);
          const parentPath = (entity as DatasourceTableUIModel)?.$parent;

          return [
            { path, prefix: true },
            { path: parentPath, directChanges: true },
          ].filter((change) => !!change.path);
        });

        return this.gitQuery.hasUncommittedChanges(pathsToCheck);
      }),
      switchMap((hasUncomittedChanges) => this.triggerRunWithDialog(config, hasUncomittedChanges)),
    );
  }

  stopOrchestrationRun(runId: OrchestrationRunUIModel['id']) {
    return this.orchestrationRunHttpService.stopRun(runId).pipe(
      this.errorHandlerService.catchError(
        '[OrchestrationRunService.stopOrchestrationRun]: could not stop orchestration run',
        () => of(null),
        {
          toast: `Could not stop orchestration run`,
          context: { runId },
        },
      ),
    );
  }

  getOrchestrationRun(runId: OrchestrationRunUIModel['id']) {
    return this.orchestrationRunHttpService.getRun(runId).pipe(
      this.errorHandlerService.catchError(
        '[OrchestrationRunService.getOrchestrationRun]: could not get orchestration run',
        () => of(null),
        {
          toast: `Could not get orchestration runs`,
          context: { runId },
        },
      ),
      tap((run) => !!run && this.updateOrchestrationRunsStoreWithFreshest([run])),
      switchMap((run) => {
        if (!run?.run_id) {
          return of(null);
        }
        return this.orchestrationRunQuery
          .selectEntity(run.run_id)
          .pipe(map((storeRun) => (storeRun ? this.toUIModel(storeRun) : null)));
      }),
    );
  }

  getOrchestrationRuns(config?: OrchestrationRunsPayload) {
    this.store.setLoading(true);

    return this.orchestrationRunHttpService.getAllRuns(config).pipe(
      this.errorHandlerService.catchError(
        '[OrchestrationRunService.getOrchestrationRuns]: could not get orchestration runs',
        () => of([] as OrchestrationRunHttpModel[]),
        {
          toast: `Could not get orchestration runs`,
          context: { config },
        },
      ),
      tap((runs) => this.updateOrchestrationRunsStoreWithFreshest(runs)),
      switchMap((runs) =>
        combineLatest([of(runs), this.orchestrationRunQuery.selectAll()]).pipe(
          map(([fetchedRuns, queryRuns]) => {
            const runIdToRunEntityMap = queryRuns.reduce((acc, run) => {
              acc[run.run_id] = run;
              return acc;
            }, {} as Record<OrchestrationRunStoreModel['run_id'], OrchestrationRunStoreModel>);

            return fetchedRuns.map((fetchedRun) => runIdToRunEntityMap[fetchedRun.run_id] || fetchedRun);
          }),
          map((storeRuns) => storeRuns.filter((run) => !!run).map((storeRun) => this.toUIModel(storeRun))),
        ),
      ),
    );
  }

  getScheduledEntitiesRuns(orchestrationPaths: Array<OrchestrationStoreModel['$path']>, noOfRuns?: number) {
    this.store.setLoading(true);

    return this.orchestrationRunHttpService.getRunsBatch(orchestrationPaths, noOfRuns).pipe(
      tap((runsResponse) => {
        const allRuns = Object.entries(runsResponse)
          .map(([__, runs]) => runs)
          .flat();

        this.updateOrchestrationRunsStoreWithFreshest(allRuns);

        this.store.setLoading(false);
      }),
    );
  }

  flushOrchestrationRuns(): void {
    applyTransaction(() => {
      this.store.remove();

      this.orchestrationStore.setSkipSync(true);
      this.orchestrationQuery.getAll().forEach((orchestration) => {
        this.orchestrationStore.update(orchestration.$path, { $children: [] });
      });
      this.orchestrationStore.setSkipSync(false);
    });
  }

  private maintainFreshestOrchestrationRuns() {
    this.orchestrationRunRealtimeService
      .listen()
      .pipe(
        tap((orchestrationRunEvent) => {
          const orchestrationRun = orchestrationRunEvent.data.data.orchestration_run;
          this.updateOrchestrationRunsStoreWithFreshest([orchestrationRun]);
        }),
        this.errorHandlerService.catchError(
          '[OrchestrationRunService.maintainFreshestOrchestrationRuns]: could not update orchestration run in realtime',
          () => of(null),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private updateOrchestrationRunsStoreWithFreshest(runs: OrchestrationRunHttpModel[]): void {
    const orchestrationToRunsMap = runs.reduce((acc, run) => {
      const location = (run.payload as TriggerOrchestrationLocationPayload)?.location;
      if (location) {
        (acc[location] = acc[location] ?? []).push(run);
      }
      return acc;
    }, {} as Record<string, OrchestrationRunHttpModel[]>);

    applyTransaction(() => {
      this.store.upsertMany(runs);

      this.orchestrationStore.setSkipSync(true);
      Object.keys(orchestrationToRunsMap).forEach((orchestrationPath) => {
        const orchestrationEntityChildren = this.orchestrationQuery.getEntity(orchestrationPath)?.$children || [];
        this.orchestrationStore.update(orchestrationPath, {
          $children: uniq([
            ...(orchestrationToRunsMap[orchestrationPath] || []).map((run) => run.run_id),
            ...orchestrationEntityChildren,
          ]),
        } as OrchestrationStoreModel);
      });
      this.orchestrationStore.setSkipSync(false);
    });
  }

  private toUIModel(storeModel: OrchestrationRunStoreModel): OrchestrationRunUIModel {
    return {
      ...storeModel,
      id: storeModel.run_id,
      created_at: new Date(storeModel.created_at),
      updated_at: storeModel.updated_at ? new Date(storeModel.updated_at) : null,
      finished_at: storeModel.finished_at ? new Date(storeModel.finished_at) : null,
    };
  }

  private calculateAssetsPathsToCheck(
    runConfig: TriggerOrchestrationRunDynamicPayload | TriggerOrchestrationRunStaticPayload,
  ) {
    let assetsPathsToCheck: Observable<string[]> = of([]);
    if (isStaticPayload(runConfig)) {
      assetsPathsToCheck = of(runConfig.nodeDataArray.map((node) => node.path));
    } else if (isDynamicPayload(runConfig)) {
      const dynamicConfig = runConfig;
      assetsPathsToCheck = this.dataLineageDiagramDataService
        .getManyUpstreamAndDownstreamDependencies(dynamicConfig.assets)
        .pipe(
          map((data) =>
            Object.keys(data).map((mainAssetPath) => ({
              mainAssetPath,
              downstream: (data[mainAssetPath]?.downstreamNodes || []).map((node) => node.key),
              upstream: (data[mainAssetPath]?.upstreamNodes || []).map((node) => node.key),
            })),
          ),
          take(1),
          map((assets) => {
            const checkOfType = (featureType: Y42FeatureType.Integration | Y42FeatureType.Export) => {
              return (assetPath: string) => {
                return this.getEntity(assetPath)?.featureType === featureType;
              };
            };

            const isIntegration = checkOfType(Y42FeatureType.Integration);
            const isExport = checkOfType(Y42FeatureType.Export);
            const excludeIntegrations = dynamicConfig.excludes.integrations;
            const excludeExports = dynamicConfig.excludes.exports;

            const filterPredicate = (path: string) => {
              if (excludeIntegrations && isIntegration(path)) {
                return false;
              } else if (excludeExports && isExport(path)) {
                return false;
              }
              return true;
            };

            return uniq(
              assets.reduce((acc, asset) => {
                const newPaths = [
                  asset.mainAssetPath,
                  ...asset.downstream.filter((assetDownstream) => filterPredicate(assetDownstream)),
                  ...asset.upstream.filter((assetUpstream) => filterPredicate(assetUpstream)),
                ];
                acc = [...acc, ...newPaths];
                return acc;
              }, [] as string[]),
            );
          }),
        );
    }

    return assetsPathsToCheck;
  }

  private triggerRunWithDialog(config: TriggerOrchestrationRunPayload, uncommitedChanges: boolean) {
    const triggerRun$ = this.orchestrationRunHttpService.triggerRun(config).pipe(
      this.errorHandlerService.catchError(
        '[OrchestrationRunService.triggerOrchestrationRun]: could not trigger orchestration run',
        () => of(null),
        {
          toast: `Could not trigger orchestration run`,
          context: { config },
        },
      ),
    );

    if (!uncommitedChanges) {
      return triggerRun$;
    }

    return this.dialog
      .open(ConfirmDialogComponent, {
        width: '600px',
        panelClass: ['confirmation-pop-up'],
        data: {
          data: true,
          header_text: 'Uncommited changes',
          text: `There are some uncommited changes in assets related to this run. Do you want to run anyway?`,
          confirmation_text: 'Trigger anyway',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => triggerRun$),
        untilDestroyed(this),
      );
  }

  private getEntity(path: string) {
    return (
      this.allDatasourcesQuery.getUIEntity(path) ||
      this.allDatasourceTablesQuery.getUIEntity(path) ||
      this.exportQuery.getUIEntity(path)
    );
  }
}
